import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, TextField, Stack, Button,
  InputAdornment, Avatar, Tooltip, IconButton, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupsIcon from '@mui/icons-material/Groups';

import { searchPatients } from '../api/patientApi';
import { formatAge } from '../hooks/usePatient';
import { useSnackbar } from '../components/Snackbar';
import { useMe } from '../context/MeContext';
import PatientFormDialog from '../components/PatientFormDialog';
import DeletePatientDialog from '../components/DeletePatientDialog';
import { PageHeader, DataTable } from '../components/ui';

const initials = (name = '') => {
  const parts = String(name).trim().split(' ').filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

/** "07 Sep 2026" — a compact, unambiguous added-on date. */
const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const SEARCH_DEBOUNCE_MS = 300;

const toRow = (p) => ({
  id: p.id,
  name: p.name,
  mobileNo: p.mobileNo,
  dateOfBirth: p.dateOfBirth,
  createdAt: p.createdAt,
  source: 'local',
});

// ---- List state that survives leaving the page ------------------------------
// Search, sort, page and page size live in the URL (?q=&sort=&dir=&page=&size=),
// so the browser Back button, a refresh or a shared link reopens the list
// exactly as it was. Links that open the bare /patient (the "Back to patients"
// button, the nav tab) get the last state of this browser tab from
// sessionStorage instead.
const SORT_KEYS = ['id', 'name', 'age', 'createdAt'];
const PAGE_SIZES = [10, 25, 50];
const LIST_PARAMS = ['q', 'sort', 'dir', 'page', 'size'];
const STORAGE_KEY = 'vqura.patients.listState';
const DEFAULT_STATE = { q: '', sort: 'id', dir: 'asc', page: 0, size: 10 };

/** Reads list state from query params, falling back to defaults for anything invalid. */
const readListState = (params) => {
  const sort = params.get('sort');
  const size = Number(params.get('size'));
  const page = parseInt(params.get('page'), 10);
  return {
    q: (params.get('q') || '').trim(),
    sort: SORT_KEYS.includes(sort) ? sort : DEFAULT_STATE.sort,
    dir: params.get('dir') === 'desc' ? 'desc' : 'asc',
    page: Number.isFinite(page) && page > 1 ? page - 1 : 0, // URL is 1-based
    size: PAGE_SIZES.includes(size) ? size : DEFAULT_STATE.size,
  };
};

/** Only non-default values go in the URL, so the plain list stays /patient. */
const toListParams = ({ q, sort, dir, page, size }) => {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (sort !== DEFAULT_STATE.sort) params.set('sort', sort);
  if (dir !== DEFAULT_STATE.dir) params.set('dir', dir);
  if (page > 0) params.set('page', String(page + 1));
  if (size !== DEFAULT_STATE.size) params.set('size', String(size));
  return params;
};

const initialListState = (params) => {
  if (LIST_PARAMS.some((k) => params.has(k))) return readListState(params);
  try {
    const saved = window.sessionStorage.getItem(STORAGE_KEY);
    if (saved) return readListState(new URLSearchParams(saved));
  } catch (e) { /* storage blocked: start fresh */ }
  return DEFAULT_STATE;
};

/**
 * Patients are searched and paged on the server. This page used to load the
 * first 1000 patients (oldest first) and filter them in the browser, so any
 * patient beyond that cap never appeared in the list or in search.
 * Sorting is server-side too (default: patient ID ascending).
 */
const Patient = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initial] = useState(() => initialListState(searchParams));
  const { showSnackbar } = useSnackbar();
  const { can } = useMe();
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  // Unfiltered count for the header badge; the pager shows the match count.
  const [clinicTotal, setClinicTotal] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initial.q);
  const [debouncedSearch, setDebouncedSearch] = useState(initial.q);
  const [page, setPage] = useState(initial.page);
  const [rowsPerPage, setRowsPerPage] = useState(initial.size);
  const [refreshKey, setRefreshKey] = useState(0);
  // Default: patient ID ascending. Users can sort by ID, name, age or date added.
  const [sortBy, setSortBy] = useState(initial.sort);
  const [sortDirection, setSortDirection] = useState(initial.dir);
  // Only the latest request may update the table, so a slow response for
  // "yu" can't overwrite the results for "yusuf".
  const requestSeq = useRef(0);
  const [open, setOpen] = useState(false);
  // The patient being corrected; null means the dialog is in "add" mode.
  const [editing, setEditing] = useState(null);

  const canEdit = can('patient:update');
  const canDelete = can('patient:delete');
  const [deleting, setDeleting] = useState(null);

  // Mirror the list state into the URL and this tab's storage. `replace` keeps
  // typing and paging out of the browser history, so Back leaves the list
  // instead of stepping through every keystroke.
  useEffect(() => {
    const next = toListParams({
      q: debouncedSearch, sort: sortBy, dir: sortDirection, page, size: rowsPerPage,
    });
    const nextString = next.toString();
    // Keep any unrelated params another feature may have put on the URL.
    const merged = new URLSearchParams(searchParams);
    LIST_PARAMS.forEach((k) => merged.delete(k));
    next.forEach((v, k) => merged.set(k, v));
    if (merged.toString() !== searchParams.toString()) {
      setSearchParams(merged, { replace: true });
    }
    try {
      window.sessionStorage.setItem(STORAGE_KEY, nextString);
    } catch (e) { /* storage blocked: URL still holds the state */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, sortBy, sortDirection, page, rowsPerPage]);

  // Opened with a search already applied (restored state): the header badge
  // still needs the unfiltered clinic total, which the list fetch won't give.
  useEffect(() => {
    if (!debouncedSearch || clinicTotal !== undefined) return undefined;
    let cancelled = false;
    searchPatients({ search: '', limit: 1, offset: 0 })
      .then((res) => {
        if (!cancelled && res.data?.succeeded) setClinicTotal(res.data.data?.total ?? 0);
      })
      .catch(() => { /* badge just stays hidden */ });
    return () => { cancelled = true; };
  }, [debouncedSearch, clinicTotal]);

  // Wait for a pause in typing before searching, and start from page 1.
  useEffect(() => {
    const term = search.trim();
    if (term === debouncedSearch) return undefined;
    const t = setTimeout(() => {
      setDebouncedSearch(term);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search, debouncedSearch]);

  useEffect(() => {
    const seq = ++requestSeq.current;

    (async () => {
      try {
        const res = await searchPatients({
          search: debouncedSearch,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          sortField: sortBy,
          sortDirection,
        });
        if (seq !== requestSeq.current) return;

        if (!res.data?.succeeded) {
          showSnackbar('Could not load patients.', 'error');
          return;
        }

        const { items = [], total: matched = 0 } = res.data.data || {};
        // Deleting the last row on the last page leaves it empty: step back one.
        if (items.length === 0 && page > 0 && matched > 0) {
          setPage((p) => Math.max(0, p - 1));
          return;
        }
        setPatients(items.map(toRow));
        setTotal(matched);
        if (!debouncedSearch) setClinicTotal(matched);
      } catch (error) {
        if (seq === requestSeq.current) showSnackbar('Could not load patients.', 'error');
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, page, rowsPerPage, refreshKey, sortBy, sortDirection]);

  const handleSortChange = (key, direction) => {
    setSortBy(key);
    setSortDirection(direction);
    setPage(0);
  };

  const handleDeleted = (p) => {
    setDeleting(null);
    showSnackbar(`${p.name || 'Patient'} (ID ${p.id}) deleted.`, 'success');
    if (debouncedSearch) setClinicTotal((t) => (t === undefined ? t : Math.max(0, t - 1)));
    setRefreshKey((k) => k + 1);
  };

  const openAdd = () => { setEditing(null); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setOpen(true); };
  const closeDialog = () => { setOpen(false); setEditing(null); };

  /**
   * Edit updates the row in place. Add refetches so the new patient lands
   * where the current sort puts it, with the counts kept honest.
   */
  const handleSaved = (saved) => {
    const exists = patients.some((p) => p.id === saved.id);
    if (exists) {
      setPatients((prev) => prev.map((p) => (p.id === saved.id ? { ...p, ...saved } : p)));
      return;
    }
    if (debouncedSearch) setClinicTotal((t) => (t === undefined ? t : t + 1));
    setPage(0);
    setRefreshKey((k) => k + 1);
  };

  const columns = [
    {
      key: 'id',
      header: 'Patient ID',
      sortKey: 'id',
    },
    {
      key: 'name',
      header: 'Patient',
      sortKey: 'name',
      primary: true,
      render: (p) => (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: 'grey.100', color: 'text.secondary', fontWeight: 700, width: 36, height: 36, fontSize: 13 }}>
            {initials(p.name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>{p.name || '—'}</Typography>
            {!p.dateOfBirth && (
              <Typography variant="caption" color="text.secondary">{`ID ${p.id}`}</Typography>
            )}
          </Box>
        </Stack>
      ),
    },
    { key: 'mobileNo', header: 'Mobile' },
    {
      key: 'age',
      header: 'Age',
      sortKey: 'age',
      render: (p) => (p.dateOfBirth ? formatAge(p.dateOfBirth) : '—'),
    },
    {
      key: 'createdAt',
      header: 'Added',
      sortKey: 'createdAt',
      hideBelow: 'md',
      render: (p) => formatDate(p.createdAt),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (p) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            variant="outlined"
            startIcon={<AssignmentIcon />}
            onClick={() => navigate(`/patient/${p.id}/assess`)}
          >
            Assess
          </Button>
          <Tooltip title="Assessment history">
            <IconButton size="small" onClick={() => navigate(`/patient/${p.id}/history`)}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEdit && p.source === 'local' && (
            <Tooltip title="Edit details">
              <IconButton size="small" onClick={() => openEdit(p)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && p.source === 'local' && (
            <Tooltip title="Delete patient">
              <IconButton size="small" aria-label={`Delete ${p.name || 'patient'}`} onClick={() => setDeleting(p)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Patients"
        count={loading ? undefined : clinicTotal}
        subtitle="Open a patient to see their developmental profile, or start an assessment"
        actions={
          <Button variant="contained" startIcon={<PersonAddAlt1Icon />} onClick={openAdd}>
            Add patient
          </Button>
        }
      />

      <TextField
        placeholder="Search by name or mobile…"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2.5 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>
          ),
        }}
      />

      <DataTable
        columns={columns}
        rows={patients}
        rowKey={(p) => `${p.source}-${p.id}`}
        loading={loading}
        pagination
        serverSide
        totalCount={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(size) => { setRowsPerPage(size); setPage(0); }}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        onRowClick={(p) => navigate(`/patient/${p.id}`)}
        empty={{
          icon: <GroupsIcon />,
          title: search ? 'No matches' : 'No patients yet',
          body: search
            ? 'Try a different name or number.'
            : 'Add your first patient to begin an assessment.',
          action: !search && (
            <Button variant="contained" startIcon={<PersonAddAlt1Icon />} onClick={openAdd}>
              Add patient
            </Button>
          ),
        }}
      />

      <DeletePatientDialog
        patient={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={handleDeleted}
      />

      <PatientFormDialog
        open={open}
        patient={editing}
        onClose={closeDialog}
        onSaved={handleSaved}
      />
    </>
  );
};

export default Patient;
